import { useState } from "react";
import type { ChangeItem } from "../api/types";
import { Icon } from "./icons";
import { T } from "../i18n";

// GY-020 notification center. A live feed of confirmed/updated/withdrawn
// findings surfaced here — the "clear new-item link" the backlog requires —
// instead of silently mutating the map/explorer view to show a new finding.

interface Props {
  feed: ChangeItem[];
  unread: number;
  markAllRead: () => void;
  onSelect: (item: ChangeItem) => void;
}

export function NotificationCenter({ feed, unread, markAllRead, onSelect }: Props) {
  const [open, setOpen] = useState(false);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) markAllRead();
  };

  return (
    <div className="notif">
      <button className="notif-bell" aria-label={T.notifications.open} aria-expanded={open} onClick={toggle}>
        <Icon.bell size={17} />
        {unread > 0 && (
          <span className="notif-badge" aria-label={`${unread}`}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="notif-panel" role="dialog" aria-label={T.notifications.title}>
          <div className="notif-head">
            <strong>{T.notifications.title}</strong>
            <button className="link" onClick={markAllRead}>
              {T.notifications.markRead}
            </button>
          </div>
          {feed.length === 0 ? (
            <p className="muted notif-empty">{T.notifications.empty}</p>
          ) : (
            <ul className="notif-list">
              {feed.map((item, i) => (
                <li key={`${item.finding_id}-${item.revision}-${i}`}>
                  <button className="notif-item" onClick={() => { onSelect(item); setOpen(false); }}>
                    <span className={`tag tag-${item.severity}`}>{item.severity}</span>
                    <span className="notif-kind">{T.notifications.kind[item.changeKind]}</span>
                    <span className="notif-title">{item.title}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
